import React, { memo, useState, useEffect, useRef, useMemo, useCallback } from "react";
import styles from "./CommonSelect.module.scss";
import { UseFormRegisterReturn } from "react-hook-form";
import { ExpandIcon } from "../../../assets/icons";

type Props = {
	items: CommonSelectItem[];
	selected?: any;
	searchable?: boolean;
	placeholder: string;
	registerProp?: UseFormRegisterReturn<any>;
	onChange: (item: CommonSelectItem | undefined) => void;
};

export class CommonSelectItem {
	key: any;
	label: string;

	constructor(key: any, label: string) {
		this.key = key;
		this.label = label;
	}
}

const CommonSelect: React.FC<Props> = memo((props: Props) => {
	const { onChange, searchable, placeholder, registerProp, items, selected } = props;

	const [showOnTop, setShowOnTop] = useState(false);

	const [dropdownExpanded, setDropdownExpanded] = useState(false);
	const [selectedItem, setSelectedItem] = useState<CommonSelectItem | undefined>(selected);

	const [search, setSearch] = useState<string>("");
	const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
	const inputRef = useRef<HTMLInputElement>(null);
	const itemsRef = useRef<HTMLDivElement>(null);

	const filteredItems = useMemo(() => {
		return items.filter(x => x.label.toLocaleLowerCase().search(search.toLocaleLowerCase()) !== -1);
	}, [items, search]);

	const selectItem = useCallback(
		(item: CommonSelectItem, event: React.MouseEvent) => {
			setSelectedItem(item);
			setSearch("");
			setDropdownExpanded(false);
			onChange(item);
			inputRef.current?.blur();
		},
		[onChange, setSelectedItem, setSearch, setDropdownExpanded, inputRef]
	);

	function changeDropdownState(): void {
		setDropdownExpanded(!dropdownExpanded);
	}

	const close = useCallback(() => {
		setDropdownExpanded(false);
		setSearch("");
	}, []);

	function getLabel(): string {
		if (selectedItem) {
			return selectedItem.label;
		}
		return placeholder;
	}

	function handleMouseDown(event: React.MouseEvent): void {
		event.preventDefault();
	}

	function changeSearch(event: React.ChangeEvent<HTMLInputElement>): void {
		setSearch(event.target.value);
		setDropdownExpanded(true);
	}

	function getInputPlaceholder(): string {
		return selectedItem?.label ?? placeholder;
	}

	const registerKeyEventsForInput = useCallback(() => {
		let inputRefCopy = inputRef;

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "ArrowDown") {
				event.preventDefault();
				if (highlightedIndex === filteredItems.length - 1) return;
				setHighlightedIndex(prevHighlightedIndex => prevHighlightedIndex + 1);
			} else if (event.key === "ArrowUp") {
				event.preventDefault();
				if (highlightedIndex === 0) return;
				setHighlightedIndex(prevHighlightedIndex => prevHighlightedIndex - 1);
			} else if (event.key === "Enter") {
				event.preventDefault();
				selectItem(filteredItems[highlightedIndex], event as any);
			} else if (event.key === "Escape") {
				event.preventDefault();
				close();
			}
		};

		inputRefCopy.current?.addEventListener("keydown", handleKeyDown);

		return () => {
			inputRefCopy?.current?.removeEventListener("keydown", handleKeyDown);
		};
	}, [highlightedIndex, filteredItems, inputRef, selectItem, close]);

	useEffect(registerKeyEventsForInput, [registerKeyEventsForInput]);

	useEffect(() => {
		setSelectedItem(selected);
	}, [selected]);

	useEffect(() => {
		setHighlightedIndex(filteredItems.findIndex(x => x.key === selected?.key));
	}, [selected, filteredItems]);

	useEffect(() => {
		if (highlightedIndex < 3) {
			itemsRef.current?.scrollTo({ top: 0, behavior: "smooth" });
			return;
		}
		itemsRef.current?.children[highlightedIndex]?.scrollIntoView({ behavior: "smooth", block: "center" });
	});

	useEffect(() => {
		if (!dropdownExpanded) return;

		const dropdown = document.getElementById("select-button");
		if (!dropdown) return;

		const dropdownRect = dropdown.getBoundingClientRect();
		const bottomOverflow = window.innerHeight - (dropdownRect.bottom + window.innerHeight * 0.225);
		const topOverflow = dropdownRect.top - window.innerHeight * 0.225;

		if (bottomOverflow < 0) {
			setShowOnTop(true);
		} else if (topOverflow < 0) {
			setShowOnTop(false);
		} else {
			setShowOnTop(false);
		}
	}, [dropdownExpanded]);

	return (
		<div className={styles.selectContainer} onBlur={close}>
			<div tabIndex={1} className={styles.selectButton} id="select-button" onClick={changeDropdownState} onBlur={close}>
				{searchable ? (
					<input type="text" ref={inputRef} className={styles.searchInput} placeholder={getInputPlaceholder()} onChange={changeSearch} value={search} />
				) : (
					<input
						readOnly
						ref={inputRef}
						className={`${styles.searchInput} ${styles.placeholderInput}`}
						placeholder={getInputPlaceholder()}
						value={getLabel()}
					/>
				)}
				<ExpandIcon />
			</div>
			<input hidden {...registerProp} />
			{
				<div
					id="select-dropdown"
					className={`${styles.selectExpanded} ${!dropdownExpanded ? styles.selectClosed : ""} ${showOnTop ? styles.selectExpandedTop : ""}`}
					onMouseDown={handleMouseDown}
				>
					<div className={styles.selectItems} ref={itemsRef}>
						{(() => {
							if (filteredItems.length === 0) {
								return <div className={styles.selectItem}>No items found</div>;
							}

							return filteredItems.map((item, index) => (
								<div
									key={index}
									className={`${styles.selectItem} ${index === highlightedIndex ? styles.selectItemHovered : ""}`}
									onClick={event => selectItem(item, event)}
								>
									{item.label}
								</div>
							));
						})()}
					</div>
				</div>
			}
		</div>
	);
});

export default CommonSelect;
